import React from "react";
import { ABOUT_IMAGE, PRICE_LIST, HIGHLIGHTS, AMENITIES, GALLERY, LOCATION_POINTS, FLOOR_PLANS, SITE, FAQS } from "../mock";
import { Download, MapPin, ChevronRight, Check, Star, Phone, PlayCircle, Video } from "lucide-react";
import * as Icons from "lucide-react";

function SectionTitle({ eyebrow, title, subtitle }) {
  return (
    <div className="text-center mb-10">
      {eyebrow && <div className="uppercase tracking-[0.3em] text-[#C9A24B] text-xs mb-2">{eyebrow}</div>}
      <h2 className="section-heading text-3xl md:text-4xl">{title}</h2>
      <span className="gold-divider" />
      {subtitle && <p className="section-subheading max-w-2xl mx-auto">{subtitle}</p>}
    </div>
  );
}

export function Overview({ onEnquire }) {
  return (
    <section id="overview" className="py-16 md:py-24 bg-[#fafaf7]">
      <div className="max-w-7xl mx-auto px-4 md:px-6 grid md:grid-cols-2 gap-10 items-center">
        <div className="relative">
          <img src={ABOUT_IMAGE} alt="Welcome to Godrej Elaris" className="rounded-lg w-full h-[420px] object-cover shadow-lg" />
          {/* Play button — scrolls to contact form */}
          <a
            href="#contact"
            aria-label="Take a virtual walkthrough — request a call"
            className="absolute inset-0 flex items-center justify-center group"
          >
            <span className="relative flex items-center justify-center">
              <span className="absolute inline-flex h-24 w-24 rounded-full bg-white/30 animate-ping" />
              <span className="relative inline-flex items-center justify-center h-20 w-20 rounded-full bg-white/95 shadow-2xl group-hover:scale-110 transition-transform duration-300">
                <PlayCircle className="w-11 h-11 text-[#8B1E1E]" strokeWidth={1.5} />
              </span>
            </span>
            <span className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-[#8B1E1E]/95 text-white text-xs uppercase tracking-widest px-3 py-1.5 rounded backdrop-blur-sm whitespace-nowrap">
              Watch Walkthrough
            </span>
          </a>
          <div className="absolute -bottom-5 -right-5 bg-[#8B1E1E] text-white px-5 py-3 rounded hidden md:block">
            <div className="font-serif-display text-2xl font-bold">12 Acres</div>
            <div className="text-xs uppercase tracking-wider opacity-90">Of Landscaped Luxury</div>
          </div>
        </div>
        <div>
          <div className="uppercase tracking-[0.3em] text-[#C9A24B] text-xs mb-2">Overview</div>
          <h2 className="section-heading text-3xl md:text-4xl mb-4">Welcome To Godrej Elaris</h2>
          <span className="gold-divider !mx-0" />
          <p className="text-gray-600 leading-relaxed mb-4">
            Godrej Elaris Magarpatta Pune is a rising residential masterpiece located in the heart of
            Magarpatta – one of the most renowned and sought-after addresses in Pune, Maharashtra. Here you
            will discover a new stage of luxurious living, skillfully combining modern architecture with the
            serenity of nature.
          </p>
          <p className="text-gray-600 leading-relaxed mb-6">
            Built on a 12 acres of thoughtfully designed land, Godrej Elaris isn't simply another residence.
            It is a lifestyle destination that caters to the needs of modern families seeking accessibility,
            ease and luxury in equal measure.
          </p>
          <div className="flex flex-wrap gap-3">
            <button onClick={onEnquire} className="btn-primary"><Download className="w-4 h-4" /> Download Brochure</button>
            <a href={`tel:${SITE.phoneRaw}`} className="btn-gold"><Phone className="w-4 h-4" /> {SITE.phone}</a>
          </div>
        </div>
      </div>
    </section>
  );
}

export function PriceList({ onEnquire }) {
  return (
    <section id="price" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionTitle eyebrow="Price List" title="Investing In The Best Location" subtitle="Explore premium configurations crafted for modern families and business owners." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
          {PRICE_LIST.map((p) => (
            <div key={p.id} className="card-elevated p-5 text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-[#C9A24B] text-[10px] tracking-widest uppercase text-[#1a1a1a] px-3 py-1 rounded-bl font-semibold">{p.category}</div>
              <div className="mt-6 uppercase tracking-[0.2em] text-xs text-gray-500">{p.category}</div>
              <h3 className="font-serif-display text-2xl font-bold text-[#8B1E1E] my-2">{p.type}</h3>
              <p className="text-sm text-gray-500 mb-3">Size: <b className="text-gray-700">{p.size}</b></p>
              <div className="border-t border-b border-[#ecdfc9] py-3 my-3">
                <div className="font-serif-display text-2xl font-bold text-[#8B1E1E]">{p.price}</div>
                <div className="text-xs text-gray-500">{p.suffix}</div>
              </div>
              <button onClick={onEnquire} className="text-[#8B1E1E] text-sm font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all">Price Break Up <ChevronRight className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Highlights() {
  return (
    <section id="highlights" className="py-16 md:py-24 bg-[#fafaf7]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionTitle eyebrow="Highlights" title="Elevating Properties: Our Spotlight Features" subtitle="A curated selection of features and advantages that make Godrej Elaris a landmark address." />
        <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
          {HIGHLIGHTS.map((h, i) => (
            <div key={i} className="flex items-start gap-4 bg-white border border-[#ecdfc9] p-4 rounded hover:border-[#C9A24B] transition-colors">
              <div className="w-10 h-10 shrink-0 rounded-full bg-[#8B1E1E] text-white font-serif-display font-bold flex items-center justify-center">{i + 1}</div>
              <p className="text-gray-700 pt-1.5">{h}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FloorPlanSection({ onEnquire }) {
  return (
    <section id="floorplan" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionTitle eyebrow="Floor Plan" title="Thoughtfully Designed Spaces" subtitle="Spacious, Vastu-compliant residences with premium fit &amp; finish." />
        <div className="grid md:grid-cols-3 gap-6">
          {FLOOR_PLANS.map((f, i) => (
            <div key={i} className="card-elevated overflow-hidden">
              <div className="relative h-56 overflow-hidden">
                <img src={f.img} alt={f.type} className="w-full h-full object-cover blur-[2px] brightness-90" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-3 left-4 text-white">
                  <div className="font-serif-display text-2xl font-bold">{f.type}</div>
                  <div className="text-xs opacity-90">{f.size}</div>
                </div>
                <div className="absolute top-3 right-3 bg-[#C9A24B] text-[#1a1a1a] text-xs font-semibold px-2 py-1 rounded">Unlock Plan</div>
              </div>
              <div className="p-4 text-center">
                <div className="font-serif-display text-lg text-[#8B1E1E] font-bold">{f.price} <span className="text-sm text-gray-500 font-normal">Onwards</span></div>
                <button onClick={onEnquire} className="btn-primary mt-3 w-full justify-center text-sm">Request Floor Plan</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function AmenitiesSection() {
  return (
    <section id="amenities" className="py-16 md:py-24 bg-[#fafaf7]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionTitle eyebrow="Amenities" title="World-Class Amenities" subtitle="A resort-like lifestyle with 40+ curated amenities right at your doorstep." />
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {AMENITIES.map((a) => {
            const I = Icons[a.icon] || Icons.Star;
            return (
              <div key={a.name} className="card-elevated p-5 flex flex-col items-center text-center gap-2">
                <div className="w-14 h-14 rounded-full bg-[#faf1dc] border border-[#ecdfc9] flex items-center justify-center">
                  <I className="w-6 h-6 text-[#8B1E1E]" strokeWidth={1.6} />
                </div>
                <span className="text-sm font-medium text-gray-700">{a.name}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function Gallery() {
  return (
    <section id="gallery" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionTitle eyebrow="Gallery" title="Experience Godrej Elaris" subtitle="A visual tour of premium interiors, amenities and the Magarpatta lifestyle." />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {GALLERY.map((g, i) => (
            <figure key={i} className={`relative overflow-hidden rounded-lg group cursor-pointer ${i === 0 ? "md:row-span-2 md:col-span-2" : ""}`}>
              <img src={g.src} alt={g.caption} className={`w-full object-cover transition-transform duration-500 group-hover:scale-105 ${i === 0 ? "h-full min-h-[300px]" : "h-44 md:h-52"}`} />
              <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent text-white p-3 text-sm font-medium">
                {g.caption}
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ScheduleCTA({ onEnquire }) {
  return (
    <section className="py-12 md:py-16 bg-[#8B1E1E] text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #C9A24B 0, transparent 40%), radial-gradient(circle at 80% 80%, #C9A24B 0, transparent 40%)" }} />
      <div className="relative max-w-5xl mx-auto px-4">
        <div className="grid md:grid-cols-3 gap-6 items-center">
          {/* Walkthrough Sample Flat — icon-first CTA */}
          <a href="#contact" className="group flex flex-col items-center text-center bg-white/5 hover:bg-white/10 border border-[#C9A24B]/40 hover:border-[#C9A24B] rounded-lg p-5 transition-colors">
            <div className="w-16 h-16 rounded-full bg-[#C9A24B] text-[#1a1a1a] flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <Video className="w-7 h-7" strokeWidth={1.6} />
            </div>
            <div className="font-serif-display text-lg font-bold text-white leading-tight">Walk-through Sample Flat</div>
            <div className="text-xs text-[#f3e7cb] mt-1">Book a guided tour today</div>
          </a>

          {/* Central heading */}
          <div className="text-center">
            <Star className="w-8 h-8 text-[#C9A24B] mx-auto mb-3" />
            <h3 className="font-serif-display text-3xl md:text-4xl font-bold mb-2">Schedule A Visit</h3>
            <p className="text-[#f3e7cb] italic text-sm">Godrej Elaris At Magarpatta–Mundhwa, Pune</p>
            <div className="flex flex-wrap gap-2 justify-center mt-4">
              <a href="#site-visit" className="btn-gold text-sm px-4 py-2">Book A Site Visit</a>
              <a href={`tel:${SITE.phoneRaw}`} className="btn-primary border border-white/30 text-sm px-4 py-2"><Phone className="w-4 h-4" /> Call</a>
            </div>
          </div>

          {/* Download brochure — icon-first CTA */}
          <button onClick={onEnquire} className="group flex flex-col items-center text-center bg-white/5 hover:bg-white/10 border border-[#C9A24B]/40 hover:border-[#C9A24B] rounded-lg p-5 transition-colors">
            <div className="w-16 h-16 rounded-full bg-[#C9A24B] text-[#1a1a1a] flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <Download className="w-7 h-7" strokeWidth={1.6} />
            </div>
            <div className="font-serif-display text-lg font-bold text-white leading-tight">Download Brochure</div>
            <div className="text-xs text-[#f3e7cb] mt-1">Full price list + floor plans</div>
          </button>
        </div>
      </div>
    </section>
  );
}

export function LocationSection() {
  return (
    <section id="location" className="py-16 md:py-24 bg-[#fafaf7]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionTitle eyebrow="Location Advantage" title="Prime Address. Everyday Convenience." subtitle="Magarpatta–Mundhwa connects you to Pune's best IT parks, schools, hospitals and malls." />
        <div className="grid lg:grid-cols-5 gap-8 items-start">
          <div className="lg:col-span-3 rounded-lg overflow-hidden shadow-lg border border-[#ecdfc9]">
            <iframe
              title="Magarpatta Pune Map"
              src="https://www.google.com/maps?q=Magarpatta+City,+Hadapsar,+Pune&output=embed"
              className="w-full h-[420px]"
              loading="lazy"
            />
          </div>
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {LOCATION_POINTS.map((l, i) => (
              <div key={i} className="bg-white border border-[#ecdfc9] rounded p-3 flex items-center gap-3 hover:border-[#C9A24B] transition-colors">
                <div className="w-9 h-9 rounded-full bg-[#faf1dc] flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4 text-[#8B1E1E]" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-800 truncate">{l.place}</div>
                  <div className="text-xs text-[#8B1E1E] font-medium">{l.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function FAQSection() {
  const [open, setOpen] = React.useState(0);
  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <SectionTitle eyebrow="FAQs" title="Frequently Asked Questions" />
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <div key={i} className="border border-[#ecdfc9] rounded overflow-hidden">
              <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between p-4 text-left hover:bg-[#fafaf7]">
                <span className="font-semibold text-gray-800 text-sm md:text-base">{f.q}</span>
                <ChevronRight className={`w-5 h-5 text-[#8B1E1E] transition-transform ${open === i ? "rotate-90" : ""}`} />
              </button>
              {open === i && <div className="px-4 pb-4 text-sm text-gray-600 leading-relaxed">{f.a}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function TrustBadges() {
  const items = [
    { v: "40+", l: "Years Legacy" },
    { v: "12 Acres", l: "Prime Land" },
    { v: "30+", l: "Floors Each Tower" },
    { v: "4.9★", l: "Buyer Rating" },
  ];
  return (
    <section className="bg-white py-10 border-y border-[#ecdfc9]">
      <div className="max-w-6xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6">
        {items.map((i, x) => (
          <div key={x} className="text-center">
            <div className="font-serif-display text-3xl md:text-4xl font-bold text-[#8B1E1E]">{i.v}</div>
            <div className="text-xs uppercase tracking-widest text-gray-500 mt-1">{i.l}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
